import { interiorWoodRowLabel } from "../domain/finishes/applyInteriorWood";
import { EXTERIOR_TREATMENT_MATERIAL_KEY } from "../domain/finishes/calculateExteriorTreatment";
import { DEFAULT_FINISHES } from "../domain/finishes/types";
import { calculateFoundation } from "../domain/foundation/calculateFoundation";
import { resolveWallHeightM } from "../domain/geometry/buildingDimensions";
import {
	BENCH_FRAME_HEIGHT_MM,
	BENCH_FRAME_THICKNESS_MM,
	BENCH_LEDGER_HEIGHT_MM,
	BENCH_SLAT_THICKNESS_MM,
	BENCH_SLAT_WIDTH_MM,
	type InteriorRoom,
	resolveEffectiveLayout,
} from "../domain/interior";
import { quantitiesForRun } from "../domain/interior/deriveBenchMaterials";
import {
	createRectRoofGeometry,
	type RectRoofGeometry,
} from "../domain/roof/rectRoofGeometry";
import { DEFAULT_ROOF_CONFIG_BY_TYPE } from "../domain/roof/roofConfig";
import type {
	EngineAuditMetadata,
	EngineEvidenceRowConfig,
	EngineMaterialClassification,
	EngineMaterialDescriptor,
	EngineVisualMapping,
} from "./auditTypes";
import {
	ENGINE_CC_M as CC,
	ENGINE_CLADDING_COV_M as CLAD_COV,
	ENGINE_FACADE_FURRING_CC_M,
	ENGINE_INTERIOR_BATTEN_CC_M,
	ENGINE_JOIST_W_M as JOIST_W,
	ENGINE_STUD_D_M as STUD_D,
} from "./constants";
import {
	calculateVentilatedFacadeQuantities,
	defaultStudLinesByWall,
} from "./facadeQuantities";
import { calculateInteriorLiningQuantities } from "./interiorQuantities";
import { resolveSubfloorBayCount, resolveSubfloorLedgerLm } from "./subfloor";
import type {
	BuildInput,
	CalculationResult,
	RawCalculationResult,
} from "./types";

const BASE_RECTANGULAR_VISUAL_MAPPINGS: EngineVisualMapping[] = [
	{ prefix: "detail/sauna-lamp", category: "systems.electrical", status: "visual-backed" },
	{ prefix: "detail/shower", category: "systems.plumbing", status: "manual-placeholder" },
	{
		prefix: "detail/foundation/beam-",
		category: "foundation.beams",
		status: "visual-backed",
	},
	{
		prefix: "detail/foundation/plinth-",
		category: "foundation.plinths",
		status: "visual-backed",
	},
	{
		prefix: "detail/foundation/shoe-",
		category: "foundation.hardware",
		status: "visual-backed",
	},
	{
		prefix: "detail/floor-joist/",
		category: "structure.floor-joists",
		status: "visual-backed",
	},
	{
		prefix: "detail/floor-panel/",
		category: "structure.floor-surface",
		status: "visual-backed",
	},
	{
		prefix: "detail/subfloor/",
		category: "structure.subfloor-bays",
		status: "visual-backed",
	},
	{
		prefix: "detail/subfloor-ledger/",
		category: "structure.subfloor-ledgers",
		status: "visual-backed",
	},
	{
		prefix: "detail/framing/",
		category: "framing.wall-framing",
		status: "visual-backed",
	},
	{
		prefix: "detail/ceiling-panel/",
		category: "interior.panel",
		status: "visual-backed",
	},
	{
		prefix: "detail/ceiling-trim/",
		category: "interior.ceiling-trim",
		status: "visual-backed",
	},
	{
		prefix: "detail/interior-batten/square-ceiling-",
		category: "interior.panel",
		status: "visual-backed",
	},
	{
		prefix: "detail/interior-batten/two-room-ceiling-",
		category: "interior.panel",
		status: "visual-backed",
	},
	{
		prefix: "detail/interior-batten/",
		category: "interior.support",
		status: "visual-backed",
	},
	{
		prefix: "detail/panel/",
		category: "interior.panel",
		status: "visual-backed",
	},
	{
		prefix: "detail/vapor-barrier/",
		category: "systems.vapor-barrier",
		status: "visual-backed",
	},
	{
		prefix: "detail/insulation/",
		category: "systems.insulation",
		status: "visual-backed",
	},
	{
		prefix: "detail/wind-barrier/",
		category: "exterior.wind-barrier",
		status: "visual-backed",
	},
	{
		prefix: "detail/furring-horizontal/",
		category: "exterior.furring",
		status: "visual-backed",
	},
	{
		prefix: "detail/furring/",
		category: "exterior.furring",
		status: "visual-backed",
	},
	{
		prefix: "detail/cladding/",
		category: "exterior.cladding",
		status: "visual-backed",
	},
	{
		prefix: "detail/exterior/",
		category: "exterior.cladding",
		status: "visual-backed",
	},
	{
		prefix: "detail/opening-trim/",
		category: "openings.trim",
		status: "visual-backed",
	},
	{
		prefix: "detail/opening-glass/",
		category: "openings.glass",
		status: "visual-backed",
	},
	{
		// A window unit's karm — part of the product the Fönster row buys.
		prefix: "detail/window-frame/",
		category: "openings.glass",
		status: "visual-backed",
	},
	{
		// A helglasdörr's pane and karm — part of the door, not a window.
		prefix: "detail/door-glass/",
		category: "openings.doors",
		status: "visual-backed",
	},
	{
		prefix: "detail/door-frame/",
		category: "openings.doors",
		status: "visual-backed",
	},
	{
		prefix: "detail/panorama-glass/",
		category: "openings.panorama_glass",
		status: "visual-backed",
	},
	{
		prefix: "detail/procedural-door/",
		category: "openings.doors",
		status: "visual-backed",
	},
	{
		prefix: "detail/procedural-door-",
		category: "openings.doors",
		status: "visual-backed",
	},
	{
		prefix: "detail/base-trim/",
		category: "exterior.base-trim",
		status: "visual-backed",
	},
	{ prefix: "detail/eave/", category: "roof.soffit", status: "visual-backed" },
	{
		prefix: "detail/roof-joist/",
		category: "roof.joists",
		status: "visual-backed",
	},
	{
		prefix: "detail/roof-truss/",
		category: "roof.joists",
		status: "visual-backed",
	},
	{
		prefix: "detail/roof-plane/",
		category: "roof.decking",
		status: "visual-backed",
	},
	{
		prefix: "detail/roof-underlay/",
		category: "roof.underlay",
		status: "visual-backed",
	},
	{
		prefix: "detail/roof-covering/",
		category: "roof.covering",
		status: "visual-backed",
	},
	{
		prefix: "detail/roof-flashing/",
		category: "roof.flashings",
		status: "visual-backed",
	},
	{
		prefix: "detail/bench-",
		category: "interior.bench",
		status: "visual-backed",
	},
	{
		prefix: "detail/heater/",
		category: "systems.heater",
		status: "visual-backed",
	},
	{
		prefix: "detail/shield/",
		category: "systems.heat-shield",
		status: "visual-backed",
	},
	{
		prefix: "detail/chimney/",
		category: "systems.chimney",
		status: "visual-backed",
	},
	{
		prefix: "detail/vent/",
		category: "systems.ventilation",
		status: "visual-backed",
	},
];

const SQUARE_VISUAL_MAPPINGS: EngineVisualMapping[] = [
	...BASE_RECTANGULAR_VISUAL_MAPPINGS,
	{ prefix: "detail/entry/", category: "entry.steps", status: "visual-backed" },
	{
		prefix: "detail/silhouette/square-fascia",
		category: "roof.fascia",
		status: "visual-backed",
	},
	{
		prefix: "detail/silhouette/square-roof-seam-",
		category: "roof.seams",
		status: "visual-backed",
	},
	{
		prefix: "detail/silhouette/square-corner-",
		category: "exterior.corner-trim",
		status: "visual-backed",
	},
];

const TWO_ROOM_VISUAL_MAPPINGS: EngineVisualMapping[] = [
	...BASE_RECTANGULAR_VISUAL_MAPPINGS,
	{ prefix: "detail/entry/", category: "entry.steps", status: "visual-backed" },
	{
		prefix: "detail/divider-panel/",
		category: "interior.panel",
		status: "visual-backed",
	},
	{
		prefix: "detail/divider-door",
		category: "openings.doors",
		status: "visual-backed",
	},
	{
		prefix: "detail/silhouette/two-room-fascia",
		category: "roof.fascia",
		status: "visual-backed",
	},
	{
		prefix: "detail/silhouette/two-room-roof-ridge",
		category: "roof.ridge",
		status: "visual-backed",
	},
	{
		prefix: "detail/silhouette/two-room-corner-",
		category: "exterior.corner-trim",
		status: "visual-backed",
	},
];

function classifyRectangularMaterial(
	material: EngineMaterialDescriptor,
): EngineMaterialClassification {
	const label = material.material.toLowerCase();

	// Bench rows are classified by their stable key, not by label substring.
	// Substring matching cannot do this correctly: "Lavbärlina" contains
	// "bärlina" and landed in `foundation.beams` — a bench ledger audited as a
	// pressure-treated foundation bearer, and green, because that category has
	// meshes too. "Lavskruv" contains "lav" and would have claimed to be drawn
	// timber. Any future bench row is one collision away from the same.
	if (material.materialKey === "electrical.fixture") {
		return { category: "systems.electrical", status: "visual-backed" };
	}
	if (material.materialKey?.startsWith("interior.bench")) {
		return material.materialKey === "interior.bench-fasteners"
			? { category: "bundles.consumables", status: "derived-nonvisual" }
			: { category: "interior.bench", status: "visual-backed" };
	}

	// Skorstensväxling: by stable key, so a label in either market lands here.
	// The headers are drawn as `detail/roof-joist/*-chimney-header-*`, the
	// noggins as `detail/interior-batten/*-ceiling-chimney-noggin-*`.
	if (material.materialKey === "framing.chimney-trimmer") {
		return { category: "roof.joists", status: "visual-backed" };
	}
	if (material.materialKey === "interior.ceiling-batten.chimney-trimmer") {
		return { category: "interior.panel", status: "visual-backed" };
	}

	// Facade colour (issue 134): by stable key, not by label substring. It
	// changes how the cladding LOOKS but adds no mesh of its own, so it is a
	// consumable — and it has to be classified, because an unmatched label
	// falls through to "uncategorized", which the BOM↔3D parity gate reads as
	// a silent claim. Keying off the name would make that gate fail on a copy
	// edit to the row's Swedish parenthetical.
	if (material.materialKey === EXTERIOR_TREATMENT_MATERIAL_KEY) {
		return { category: "bundles.consumables", status: "derived-nonvisual" };
	}

	// Isolering and folie are rendered layers as of issue 114 (`detail/insulation/`
	// and `detail/vapor-barrier/`), not derived consumption — the product rule is
	// that everything in the material list is visualised.
	if (label.includes("stenullsisolering"))
		return { category: "systems.insulation", status: "visual-backed" };
	if (label.includes("bastufolie"))
		return { category: "systems.vapor-barrier", status: "visual-backed" };
	if (label.includes("kabel"))
		return { category: "systems.electrical", status: "derived-nonvisual" };
	if (label.includes("dörr") && label.includes("tillbehör")) {
		return {
			category: "bundles.doors-and-accessories",
			status: "manual-placeholder",
		};
	}
	if (label.includes("dörrar") && label.includes("tillbehör")) {
		return {
			category: "bundles.doors-and-accessories",
			status: "manual-placeholder",
		};
	}
	if (label.includes("aggregat") || label.includes("ventiler")) {
		return { category: "bundles.systems", status: "manual-placeholder" };
	}
	if (label.includes("råspont"))
		return { category: "roof.decking", status: "visual-backed" };
	if (label.includes("underlagspapp"))
		return { category: "roof.underlay", status: "visual-backed" };
	if (label.includes("ytpapp") || label.includes("takplåt")) {
		return { category: "roof.covering", status: "visual-backed" };
	}
	if (
		label.includes("takfotsplåt") ||
		label.includes("vindskiveplåt") ||
		label.includes("nockbeslag")
	) {
		return { category: "roof.flashings", status: "visual-backed" };
	}
	if (label.includes("plint"))
		return { category: "foundation.plinths", status: "visual-backed" };
	if (label.includes("bärlina"))
		return { category: "foundation.beams", status: "visual-backed" };
	if (label.includes("golv- & takbjälkar"))
		return { category: "structure.joists", status: "visual-backed" };
	if (label.includes("golvbjälkar"))
		return { category: "structure.floor-joists", status: "visual-backed" };
	if (label.includes("takbjälkar"))
		return { category: "roof.joists", status: "visual-backed" };
	if (label.includes("väggreglar"))
		return { category: "framing.wall-framing", status: "visual-backed" };
	if (
		label.includes("takgles") ||
		(label.includes("spikläkt") && label.includes("innertak"))
	) {
		return { category: "interior.panel", status: "visual-backed" };
	}
	// Taklist / bomlist got geometry in issue 115 (`detail/ceiling-trim/`);
	// it used to fall through to uncategorized/derived-nonvisual.
	if (label.includes("taklist") || label.includes("bomlist")) {
		return { category: "interior.ceiling-trim", status: "visual-backed" };
	}
	// "Inv. Spikläkt / Glespanel" contains "spikläkt" too — classify the interior
	// lying batten layer before the exterior ströläkt/spikläkt catch-all below,
	// or the interior rows land in exterior.furring.
	if (
		label.includes("glespanel") ||
		(label.includes("spikläkt") && label.includes("inv."))
	) {
		return { category: "interior.support", status: "visual-backed" };
	}
	if (label.includes("vindskydd") || label.includes("träfiberskiva"))
		return { category: "exterior.wind-barrier", status: "visual-backed" };
	if (label.includes("spikläkt") || label.includes("ströläkt"))
		return { category: "exterior.furring", status: "visual-backed" };
	if (label.includes("bastupanel") || label.includes("invändig panel")) {
		return { category: "interior.panel", status: "visual-backed" };
	}
	if (label.includes("ytterpanel"))
		return { category: "exterior.cladding", status: "visual-backed" };
	if (label.includes("knutbrädor"))
		return { category: "exterior.corner-trim", status: "visual-backed" };
	if (label.includes("nederkantsavslut"))
		return { category: "exterior.base-trim", status: "visual-backed" };
	if (label.includes("vindskivor"))
		return { category: "roof.fascia", status: "visual-backed" };
	if (label.includes("takfotsavslut") || label.includes("undertak")) {
		return { category: "roof.soffit", status: "visual-backed" };
	}
	if (label.includes("lav"))
		return { category: "interior.bench", status: "visual-backed" };
	// The trossbotten rows have their OWN meshes (`detail/subfloor-ledger/` and
	// `detail/subfloor/`), so they must not fall into the golvspont's
	// floor-surface bucket. Läkten is tested first — "Trossbottenläkt" also
	// contains "trossbotten".
	if (label.includes("trossbottenläkt")) {
		return { category: "structure.subfloor-ledgers", status: "visual-backed" };
	}
	if (label.includes("trossbotten") || label.includes("undergolv")) {
		return { category: "structure.subfloor-bays", status: "visual-backed" };
	}
	if (label.includes("golv")) {
		return { category: "structure.floor-surface", status: "visual-backed" };
	}

	// ─── Rows that ARE drawn but used to fall through to `uncategorized` ───
	// A row without a category can never be parity-checked against the model, and
	// the fall-through asserted `derived-nonvisual` ("computed, not drawn") by
	// default — which was wrong for every label below. Verified against real mesh
	// names in the audit sweep, not assumed. See the BOM/geometry parity note.
	if (label.includes("mittregel")) {
		// Panorama mullion post — drawn as detail/framing/*, same family as the
		// wall studs it sits between.
		return { category: "framing.wall-framing", status: "visual-backed" };
	}
	if (label.includes("avväxling")) {
		// detail/framing/<wall>-header-<opening>
		return { category: "framing.wall-framing", status: "visual-backed" };
	}
	if (
		label.includes("bastudörr") ||
		label.includes("ytterdörr") ||
		label.includes("mellendörr") ||
		label.includes("mellandörr")
	) {
		// detail/procedural-door-*, detail/divider-door
		return { category: "openings.doors", status: "visual-backed" };
	}
	if (label.includes("foder")) {
		// detail/opening-trim/*  (dörrfoder, fönsterfoder)
		return { category: "openings.trim", status: "visual-backed" };
	}
	if (label.startsWith("panoramaglas")) {
		// detail/panorama-glass/* and the panes' karm (detail/window-frame/*),
		// the same pair a Fönster row covers.
		return { category: "openings.glass", status: "visual-backed" };
	}
	if (label.startsWith("fönster")) {
		// detail/opening-glass
		return { category: "openings.glass", status: "visual-backed" };
	}
	if (label.includes("stolpsko")) {
		// detail/foundation/shoe-*
		return { category: "foundation.hardware", status: "visual-backed" };
	}
	if (
		label.includes("bastuskorsten") ||
		label.includes("sauna chimney")
	) {
		// detail/chimney/*, detail/roof-flashing
		return { category: "systems.chimney", status: "visual-backed" };
	}
	if (label.startsWith("strålskydd")) {
		// detail/shield/*
		return { category: "systems.heat-shield", status: "visual-backed" };
	}
	if (label.includes("styrpanel")) {
		return { category: "systems.heater", status: "visual-backed" };
	}
	// Structural blocking that the BOM buys but nothing draws — real lumber, so it
	// must not be filed under consumables. Flagged as a modelling gap, not a
	// classification one: see the BOM/geometry parity note.
	if (label.includes("kortling")) {
		return { category: "framing.blocking", status: "derived-nonvisual" };
	}
	// ─── Rows that are genuinely NOT drawn ───
	// Consumables and site work. Classified explicitly so the fall-through below
	// stays empty: an unknown label must fail loudly, not be assumed non-visual.
	if (
		label.includes("markduk") ||
		label.includes("dräneringsgrus") ||
		label.includes("makadam") ||
		label.includes("alutejp") ||
		label.includes("fogtejp") ||
		label.includes("skruv") ||
		label.includes("beslagspaket") ||
		label.includes("ytbehandling") ||
		label.includes("brandtätning") ||
		label.includes("sotarlucka")
	) {
		return { category: "bundles.consumables", status: "derived-nonvisual" };
	}

	return { category: "uncategorized", status: "derived-nonvisual" };
}

/**
 * Bench evidence, counted from the `BenchRun` data the model is drawn from.
 *
 * Before issue 131 the `interior.bench` rows had no explicit evidence: they fell
 * through to the generic builder, which reads `parseQuantity(row.quantity)` —
 * the audit "verified" the BOM by restating it, and would have restated a wrong
 * number just as confidently. The rows below re-derive each quantity from the
 * runs' own geometry, so a change in `deriveBenchMaterials` that the renderer
 * does not follow (or the reverse) surfaces as a difference instead of as
 * agreement.
 *
 * `interiorMeasurements` carries the rooms and heater footprint the layout was
 * measured in, so what is counted here are the CLIPPED runs the BOM billed —
 * not the full-length runs the layout stores.
 *
 * Empty for the barrel engine and for any result without a layout; those rows
 * keep falling through to the generic path.
 */
function interiorBenchRows(
	idPrefix: string,
	calculation: CalculationResult | RawCalculationResult,
	finishes = DEFAULT_FINISHES,
): EngineEvidenceRowConfig[] {
	const layout = calculation.interiorLayout;
	// The raw result has the layout; the measurements are added by the enrichment
	// chain, so a pre-enrichment call legitimately has only the former.
	const measurements =
		"interiorMeasurements" in calculation
			? calculation.interiorMeasurements
			: undefined;
	if (!layout || !measurements) return [];

	// One set of rows PER ROOM. `deriveBenchMaterials` is called once per room
	// and tags each quantity with its own suffix ("(Bastudel)" / "(Relaxdel)"),
	// so a two-room design has two of every bench row. Summing the rooms here
	// produced one audit number that matched neither.
	return measurements.rooms.flatMap((room: InteriorRoom) =>
		interiorBenchRowsForRoom(
			measurements.rooms.length > 1 ? `${idPrefix}-${room.id}` : idPrefix,
			resolveEffectiveLayout(
				layout,
				room,
				room.id === "sauna" ? measurements.heaterSize : null,
			).runs,
			room,
			finishes,
		),
	);
}

function interiorBenchRowsForRoom(
	idPrefix: string,
	runs: ReturnType<typeof resolveEffectiveLayout>["runs"],
	room: InteriorRoom,
	finishes = DEFAULT_FINISHES,
	splitSpecies = true,
): EngineEvidenceRowConfig[] {
	if (runs.length === 0) return [];
	if (splitSpecies && runs.some(({ effective }) => effective.woodSpecies)) {
		const groups = new Map<string, typeof runs>();
		for (const entry of runs) {
			const species = entry.effective.woodSpecies ?? finishes.interiorWood;
			groups.set(species, [...(groups.get(species) ?? []), entry]);
		}
		const visible = (row: EngineEvidenceRowConfig) =>
			row.id.endsWith("-bench-timber") || row.id.endsWith("-bench-front");
		return [
			...interiorBenchRowsForRoom(idPrefix, runs, room, finishes, false).filter(
				(row) => !visible(row),
			),
			...[...groups].flatMap(([species, members]) =>
				interiorBenchRowsForRoom(
					`${idPrefix}-${species}`,
					members,
					room,
					{
						...finishes,
						interiorWood:
							members[0].effective.woodSpecies ?? finishes.interiorWood,
					},
					false,
				).filter(visible),
			),
		];
	}

	let slatLpm = 0;
	let skirtLpm = 0;
	let backrestLpm = 0;
	let railLpm = 0;
	let ledgerLpm = 0;
	let ledgerCount = 0;
	let frameLpm = 0;
	let joistCount = 0;
	let legCount = 0;
	for (const { effective: run } of runs) {
		const q = quantitiesForRun(run, room);
		slatLpm += q.slatLpm;
		skirtLpm += q.skirtLpm;
		backrestLpm += q.backrestLpm;
		railLpm += q.railLpm;
		ledgerLpm += q.ledgerLpm;
		ledgerCount += q.ledgerCount;
		frameLpm += q.frameLpm + q.legLpm;
		joistCount += q.joistCount;
		legCount += q.legCount;
	}

	const variables = [
		{ label: "runs", value: runs.length.toString() },
		{
			label: "runLengthsMm",
			value: runs
				.map((entry) => Math.round(entry.effective.lengthMm))
				.join(" + "),
		},
		{
			label: "runDepthsMm",
			value: runs
				.map((entry) => Math.round(entry.effective.depthMm))
				.join(" + "),
		},
		{ label: "joists", value: joistCount.toString() },
		{ label: "legs", value: legCount.toString() },
		{ label: "wallBearings", value: ledgerCount.toString() },
	];
	const frameDimension = `${BENCH_FRAME_THICKNESS_MM}×${BENCH_FRAME_HEIGHT_MM} mm`;
	/**
	 * The UNROUNDED metre total, carried alongside the rounded `rawTotal`.
	 *
	 * `rawTotal` has to be the purchase quantity, and that is `ceil()`ed — which
	 * makes a comparison against the BOM row granular to a whole metre: any
	 * per-row error under 1 lpm rounds to the same integer and passes. (Shown by
	 * driving the audit's own leg rule: only the two-room scenarios moved, both
	 * square ones stayed green.) The exact figure lets the gate compare at full
	 * precision, and it is worth showing a builder anyway.
	 */
	const exactLpm = (lpm: number) => ({
		label: "lpmExact",
		value: lpm.toFixed(6),
	});

	const roundedStock = (kind: "timber" | "front") => {
		const totals = new Map<string, number>();
		for (const { effective: run } of runs) {
			const q = quantitiesForRun(run, room);
			const key = run.woodSpecies ?? "auto";
			totals.set(
				key,
				(totals.get(key) ?? 0) +
					(kind === "timber"
						? q.slatLpm + q.skirtLpm + q.backrestLpm
						: q.railLpm),
			);
		}
		return [...totals.values()].reduce(
			(total, value) => total + Math.ceil(value),
			0,
		);
	};
	const rows: EngineEvidenceRowConfig[] = [
		{
			id: `${idPrefix}-bench-timber`,
			category: "interior.bench",
			sourceLabel: interiorWoodRowLabel("Lav-virke (Kvistfritt)", finishes),
			sourceType: "material",
			dimension: `${BENCH_SLAT_THICKNESS_MM}×${BENCH_SLAT_WIDTH_MM} mm`,
			unit: "lpm",
			rawTotal: roundedStock("timber"),
			status: "visual-backed",
			formula:
				"Σ_authorship_group ceil(Σ_run (benchSlatCount(depth, gap) * seatCutLength + skirtBoards * (frontLength + openEnds * endLength) + backrestBoards * backrestLength + backrestSpacers * backrestHeight) / 1000)",
			variables: [
				...variables,
				{ label: "skirtLpm", value: skirtLpm.toFixed(6) },
				{ label: "backrestLpm", value: backrestLpm.toFixed(6) },
				exactLpm(slatLpm + skirtLpm + backrestLpm),
			],
			notes: [
				"Räknad ur lavlayoutens körningar — samma BenchRun-data som 3D-modellen ritar, klippt runt aggregatet.",
				"Inkluderar spjälfronten som stänger nedre lavens ben (detail/bench-skirt/*).",
			],
		},
		{
			id: `${idPrefix}-bench-ledger`,
			category: "interior.bench",
			sourceLabel: "Lavbärlina (upplag mot vägg)",
			sourceType: "material",
			dimension: `${BENCH_FRAME_THICKNESS_MM}×${BENCH_LEDGER_HEIGHT_MM} mm`,
			unit: "lpm",
			rawTotal: Math.ceil(ledgerLpm),
			status: "visual-backed",
			formula:
				"ceil(Σ_run (endWallSupports ? 2 * depth : backWallSupport ? railLength : 0) / 1000)",
			variables: [...variables, exactLpm(ledgerLpm)],
			notes: [
				"Bakre väggupplag eller två korta ändupplag mot sidoväggarna, enligt den lösta lavkonstruktionen (detail/bench-ledger/*).",
			],
		},
		{
			id: `${idPrefix}-bench-front`,
			category: "interior.bench",
			sourceLabel: interiorWoodRowLabel("Lavfront (frontbräda)", finishes),
			sourceType: "material",
			dimension: frameDimension,
			unit: "lpm",
			rawTotal: roundedStock("front"),
			status: "visual-backed",
			formula: "Σ_authorship_group ceil(Σ_run actualRailCutLength / 1000)",
			variables: [...variables, exactLpm(railLpm)],
			notes: [
				"Ändstödda långreglar löper över båda upplagen och lämnar monteringsspel mot väggen. Övriga frontbrädor löper mellan gavelreglarna (detail/bench-rail/*-front).",
			],
		},
		{
			id: `${idPrefix}-bench-frame`,
			category: "interior.bench",
			sourceLabel: "Lavstomme (reglar & ben)",
			sourceType: "material",
			dimension: frameDimension,
			unit: "lpm",
			rawTotal: Math.ceil(frameLpm),
			status: "visual-backed",
			formula:
				"ceil(Σ_run (2 * endRailCutLength + joists * joistCutLength + legs * legHeight + backFrameRail) / 1000)",
			variables: [...variables, exactLpm(frameLpm)],
			notes: [
				"Gavelreglar, tvärbalkar, ben och bakregel för lavar utan bakre väggupplag. Tvärbalkarna sitter mellan fram- och bakregeln.",
			],
		},
	];
	return rows.filter((row) => row.rawTotal > 0);
}

function foundationBeamRow(
	id: string,
	notes: string[],
	beamLengthLmLabel: string | undefined,
	input: BuildInput,
	calculation: CalculationResult | RawCalculationResult,
): EngineEvidenceRowConfig {
	const foundation =
		calculation.foundation ??
		calculateFoundation(input.dimensions.width, input.dimensions.length);
	const variables = [
		{ label: "numBeams", value: foundation.numBeams.toString() },
	];

	if (beamLengthLmLabel) {
		variables.push({ label: "beamLengthLm", value: beamLengthLmLabel });
	}

	return {
		id,
		category: "foundation.beams",
		sourceLabel: "Bärlina Tryckimpregnerad",
		sourceType: "material",
		dimension: "45×145 mm (NTR-A)",
		unit: "lpm",
		rawTotal: Math.ceil(foundation.totalBeamLengthLm * 1.05),
		status: "visual-backed",
		formula: "ceil(totalBeamLengthLm * 1.05)",
		variables,
		notes,
	};
}

// Both rectangular engines render the same lying-spikläkt korsläkt layer via
// renderHorizontalFurringStrips, so their evidence rows share one definition.
// The rawTotal is read straight from the shared BOM helper (single source of
// truth), so the evidence can never drift from the purchase quantity.
function exteriorFurringRow(
	id: string,
	W: number,
	L: number,
	roofGeometry: RectRoofGeometry,
): EngineEvidenceRowConfig {
	const { lmHorizontalFurring } = calculateVentilatedFacadeQuantities({
		W,
		L,
		studLinesByWall: defaultStudLinesByWall(W, L),
		wallArea: 0,
		roofGeometry,
	});
	const ccText = ENGINE_FACADE_FURRING_CC_M.toFixed(2);
	const rowsPerWall = (wall: "front" | "back" | "left" | "right") =>
		Math.ceil(
			roofGeometry.getMaxWallHeight(wall) / ENGINE_FACADE_FURRING_CC_M,
		) + 1;
	return {
		id,
		category: "exterior.furring",
		sourceLabel: "Utv. Spikläkt (Liggande)",
		sourceType: "material",
		dimension: "22×45 mm",
		unit: "lpm",
		rawTotal: lmHorizontalFurring,
		status: "visual-backed",
		formula: "ceil(Σ_wall wallLength * (ceil(maxWallHeight_wall / cc) + 1))",
		variables: [
			{ label: "cc", value: ccText },
			{ label: "rowsFront", value: rowsPerWall("front").toString() },
			{ label: "rowsBack", value: rowsPerWall("back").toString() },
			{ label: "rowsSide", value: rowsPerWall("left").toString() },
		],
		notes: [
			`Liggande spikläkt c/c ${(ENGINE_FACADE_FURRING_CC_M * 1000).toFixed(0)} i korsläkt — den stående panelen spikas i dessa, inte i den stående ströläkten. Radantal per vägg upp till väggens maxhöjd (nock/hög takfot).`,
		],
	};
}

// Both rectangular engines render the same LYING interior glespanel via
// renderInteriorLyingBattenStrips, so their evidence rows share one definition.
// The rawTotal comes from the shared BOM helper, so the evidence can never
// drift from the purchase quantity.
function interiorBattenRow(
	id: string,
	W: number,
	L: number,
	roofGeometry: RectRoofGeometry,
	dividerSpans?: Array<{ lengthM: number; heightM: number }>,
): EngineEvidenceRowConfig {
	const { lmInteriorBattens, rowsForWall } = calculateInteriorLiningQuantities({
		W,
		L,
		roofGeometry,
		dividerSpans,
	});
	return {
		id,
		category: "interior.support",
		sourceLabel: "Inv. Spikläkt / Glespanel",
		sourceType: "material",
		dimension: "22×45 mm",
		unit: "lpm",
		rawTotal: lmInteriorBattens,
		status: "visual-backed",
		formula: "ceil(Σ_wall wallLength * (ceil(maxWallHeight_wall / cc) + 1))",
		variables: [
			{ label: "cc", value: ENGINE_INTERIOR_BATTEN_CC_M.toFixed(2) },
			{ label: "rowsFront", value: rowsForWall("front").toString() },
			{ label: "rowsBack", value: rowsForWall("back").toString() },
			{ label: "rowsSide", value: rowsForWall("left").toString() },
		],
		notes: [
			`Liggande glespanel c/c ${(ENGINE_INTERIOR_BATTEN_CC_M * 1000).toFixed(0)} mellan bastufolien och den stående bastupanelen — panelen spikas i dessa. Radantal per vägg upp till väggens maxhöjd (nock/hög takfot).`,
		],
	};
}

function getRectangularWallMetrics(
	width: number,
	length: number,
	wallHeight: number,
	roofConfig: NonNullable<BuildInput["roofConfig"]>,
) {
	const roofGeometry = createRectRoofGeometry({
		width,
		length,
		wallHeight,
		roofConfig,
	});
	const averageWallHeight = (wall: "front" | "back" | "left" | "right") =>
		(roofGeometry.getMinWallHeight(wall) +
			roofGeometry.getMaxWallHeight(wall)) /
		2;

	return {
		roofGeometry,
		averageHeights: {
			front: averageWallHeight("front"),
			back: averageWallHeight("back"),
			left: averageWallHeight("left"),
			right: averageWallHeight("right"),
		},
		wallArea:
			width * averageWallHeight("front") +
			width * averageWallHeight("back") +
			length * averageWallHeight("left") +
			length * averageWallHeight("right"),
	};
}

export function getSquareAuditMetadata(): EngineAuditMetadata {
	return {
		visualMappings: SQUARE_VISUAL_MAPPINGS,
		classifyMaterial: classifyRectangularMaterial,
	};
}

export function getTwoRoomAuditMetadata(): EngineAuditMetadata {
	return {
		visualMappings: TWO_ROOM_VISUAL_MAPPINGS,
		classifyMaterial: classifyRectangularMaterial,
	};
}

function isOutdoorOnlyEvidenceCategory(category: string): boolean {
	return category.startsWith("exterior.") || category === "foundation.beams";
}

export function getSquareEvidenceConfig(
	input: BuildInput,
	calculation: CalculationResult | RawCalculationResult,
): EngineEvidenceRowConfig[] {
	const finishes = input.finishes ?? DEFAULT_FINISHES;
	const W = input.dimensions.width;
	const L = input.dimensions.length;
	const roofConfig = input.roofConfig ?? DEFAULT_ROOF_CONFIG_BY_TYPE.fyrkantig;
	const { roofGeometry, averageHeights, wallArea } = getRectangularWallMetrics(
		W,
		L,
		resolveWallHeightM(input.wallHeightM),
		roofConfig,
	);
	// A mono falling across the width (high wall left/right) rakes the
	// front/back walls instead of the sides: their hammarband runs up the
	// slope and the side plates stay level.
	const monoAcross = roofGeometry.monoSlope?.axis === "x";
	const slopedL =
		roofConfig.kind === "mono" && !monoAcross
			? L / Math.cos(Math.max(roofGeometry.pitchRad, 0.0001))
			: L;
	const endTopPlateL = monoAcross
		? W / Math.cos(Math.max(roofGeometry.pitchRad, 0.0001))
		: W;
	const area = W * L;
	const numStudsW = Math.ceil((W - JOIST_W) / CC) + 1;
	const numStudsL = Math.ceil((L - 0.19) / CC) + 1;

	const rows: EngineEvidenceRowConfig[] = [
		foundationBeamRow(
			"square-foundation-beams",
			["Verifierad mot namngivna foundation beams."],
			(W - 0.05).toFixed(2),
			input,
			calculation,
		),
		{
			id: "square-wall-studs-front",
			category: "framing.front-wall",
			sourceLabel: "Väggreglar, Syll, Hammarband",
			sourceType: "material",
			dimension: "45×95 mm",
			unit: "lpm",
			rawTotal: monoAcross
				? Math.ceil(numStudsW * averageHeights.front + W + endTopPlateL)
				: Math.ceil(numStudsW * averageHeights.front + W * 2),
			status: "visual-backed",
			formula: monoAcross
				? "ceil(numStudsW * frontWallHeight + W + endTopPlateLength)"
				: "ceil(numStudsW * frontWallHeight + 2 * W)",
			variables: [
				{ label: "numStudsW", value: numStudsW.toString() },
				{ label: "frontWallHeight", value: averageHeights.front.toFixed(2) },
			],
			notes: ["Framsidans stående reglar, syll och hammarband."],
		},
		{
			id: "square-wall-studs-back",
			category: "framing.back-wall",
			sourceLabel: "Väggreglar, Syll, Hammarband",
			sourceType: "material",
			dimension: "45×95 mm",
			unit: "lpm",
			rawTotal: monoAcross
				? Math.ceil(numStudsW * averageHeights.back + W + endTopPlateL)
				: Math.ceil(numStudsW * averageHeights.back + W * 2),
			status: "visual-backed",
			formula: monoAcross
				? "ceil(numStudsW * backWallHeight + W + endTopPlateLength)"
				: "ceil(numStudsW * backWallHeight + 2 * W)",
			variables: [
				{ label: "backWallHeight", value: averageHeights.back.toFixed(2) },
			],
			notes: ["Baksidans reglar och top/bottom plates."],
		},
		{
			id: "square-wall-studs-left",
			category: "framing.left-wall",
			sourceLabel: "Väggreglar, Syll, Hammarband",
			sourceType: "material",
			dimension: "45×95 mm",
			unit: "lpm",
			rawTotal: Math.ceil(numStudsL * averageHeights.left + L + slopedL),
			status: "visual-backed",
			formula:
				"ceil(numStudsL * leftWallAverageHeight + L + sideTopPlateLength)",
			variables: [
				{ label: "numStudsL", value: numStudsL.toString() },
				{ label: "sideTopPlateLength", value: slopedL.toFixed(2) },
			],
			notes: ["Vänster vägg inklusive lutande hammarband."],
		},
		{
			id: "square-wall-studs-right",
			category: "framing.right-wall",
			sourceLabel: "Väggreglar, Syll, Hammarband",
			sourceType: "material",
			dimension: "45×95 mm",
			unit: "lpm",
			rawTotal: Math.ceil(numStudsL * averageHeights.right + L + slopedL),
			status: "visual-backed",
			formula:
				"ceil(numStudsL * rightWallAverageHeight + L + sideTopPlateLength)",
			variables: [{ label: "sideTopPlateLength", value: slopedL.toFixed(2) }],
			notes: ["Höger vägg inklusive lutande hammarband."],
		},
		{
			id: "square-panel-interior",
			category: "interior.panel",
			sourceLabel: interiorWoodRowLabel("Bastupanel", finishes),
			sourceType: "material",
			dimension: "15×90 mm",
			unit: "lpm",
			rawTotal: Math.ceil((wallArea + area) * 11.5),
			status: "visual-backed",
			formula: "ceil((wallArea + floorArea) * 11.5)",
			variables: [
				{ label: "wallArea", value: wallArea.toFixed(2) },
				{ label: "floorArea", value: area.toFixed(2) },
			],
			notes: ["Invändiga väggar + innertak uttryckt i löpmeter panel."],
		},
		{
			id: "square-exterior-cladding",
			category: "exterior.cladding",
			sourceLabel: "Ytterpanel (Spontad Gran)",
			sourceType: "material",
			dimension: "22×120 mm (110mm täck)",
			unit: "lpm",
			rawTotal: Math.ceil((wallArea / CLAD_COV) * 1.1),
			status: "visual-backed",
			formula: "ceil((wallArea / claddingCoverage) * 1.1)",
			variables: [
				{ label: "wallArea", value: wallArea.toFixed(2) },
				{ label: "coverage", value: CLAD_COV.toFixed(2) },
			],
			notes: ["Fasadpanel med 10% spill."],
		},
		exteriorFurringRow("square-exterior-furring", W, L, roofGeometry),
		interiorBattenRow("square-interior-battens", W, L, roofGeometry),
		...interiorBenchRows("square", calculation, finishes),
	];
	// Issue 156: the indoor sauna draws no foundation beams and no facade.
	// Its outside (issue 174: panel on free walls, träfiberskiva against the
	// room) gets no hand-written evidence row on purpose: the BOM rows are
	// classified by label, so the parity gate compares the drawn board and
	// panel against what the BOM actually buys, not against a second formula.
	return input.siting === "indoor"
		? rows.filter((row) => !isOutdoorOnlyEvidenceCategory(row.category))
		: rows;
}

export function getTwoRoomEvidenceConfig(
	input: BuildInput,
	calculation: CalculationResult | RawCalculationResult,
): EngineEvidenceRowConfig[] {
	const finishes = input.finishes ?? DEFAULT_FINISHES;
	const W = input.dimensions.width;
	const L = input.dimensions.length;
	const H = resolveWallHeightM(input.wallHeightM);
	const roofConfig = input.roofConfig ?? DEFAULT_ROOF_CONFIG_BY_TYPE.tvarum;
	const { wallArea, roofGeometry } = getRectangularWallMetrics(
		W,
		L,
		H,
		roofConfig,
	);
	const area = W * L;
	const saunaL = L * 0.5;
	const divWallArea = (W - 0.19) * H;
	const numDivStuds = Math.ceil((W - 0.19 - JOIST_W) / CC) + 1;
	const lmPanel = Math.ceil((wallArea + divWallArea + area) * 11.5);

	return [
		foundationBeamRow(
			"two-room-foundation-beams",
			["Verifierad mot grundbalkar i två-rumsmotorn."],
			undefined,
			input,
			calculation,
		),
		{
			id: "two-room-divider",
			category: "framing.divider-wall",
			sourceLabel: "Väggreglar (Ytter+Delning)",
			sourceType: "material",
			dimension: "45×95 mm",
			unit: "lpm",
			rawTotal: Math.ceil(numDivStuds * H + W * 2),
			status: "visual-backed",
			formula: "ceil(numDivStuds * H + 2 * W)",
			variables: [{ label: "numDivStuds", value: numDivStuds.toString() }],
			notes: ["Delningsväggens reglar samt syll/hammarband."],
		},
		{
			id: "two-room-sauna-panel",
			category: "interior.sauna-panel",
			sourceLabel: interiorWoodRowLabel("Bastupanel (Bastudel)", finishes),
			sourceType: "material",
			dimension: "15×90 mm",
			unit: "lpm",
			rawTotal: Math.ceil(lmPanel * 0.5),
			status: "visual-backed",
			formula: "ceil(lmPanel * split)",
			variables: [
				{ label: "lmPanel", value: lmPanel.toString() },
				{ label: "split", value: "0.5" },
			],
			notes: ["Bastudelens panelandel."],
		},
		{
			id: "two-room-relax-panel",
			category: "interior.relax-panel",
			sourceLabel: interiorWoodRowLabel("Invändig Panel (Relaxdel)", finishes),
			sourceType: "material",
			dimension: "15×90 mm",
			unit: "lpm",
			rawTotal: Math.ceil(lmPanel * 0.5),
			status: "visual-backed",
			formula: "ceil(lmPanel * (1 - split))",
			variables: [{ label: "saunaL", value: saunaL.toFixed(2) }],
			notes: ["Relaxdelens invändiga panelandel."],
		},
		{
			id: "two-room-outer-cladding",
			category: "exterior.cladding",
			sourceLabel: "Ytterpanel (Spontad Gran)",
			sourceType: "material",
			dimension: "24×120 mm (110mm täck)",
			unit: "lpm",
			rawTotal: Math.ceil((Math.ceil(wallArea) / 0.11) * 1.1),
			status: "visual-backed",
			formula: "ceil((ceil(outerWallArea) / 0.11) * 1.1)",
			variables: [{ label: "outerWallArea", value: wallArea.toFixed(2) }],
			notes: ["Ytterfasad med 10% spillpåslag."],
		},
		exteriorFurringRow("two-room-exterior-furring", W, L, roofGeometry),
		// Divider faces only when the divider is actually built — mirrors the
		// engine's own dividerSpans gate.
		interiorBattenRow(
			"two-room-interior-battens",
			W,
			L,
			roofGeometry,
			(input.hasRelaxRoom ?? true)
				? [
						{ lengthM: W - 2 * STUD_D, heightM: H },
						{ lengthM: W - 2 * STUD_D, heightM: H },
					]
				: undefined,
		),
		{
			id: "two-room-floor-subfloor",
			category: "structure.subfloor-bays",
			sourceLabel: "Undergolv / Trossbotten",
			sourceType: "material",
			dimension: "12 mm asfaboard",
			unit: "m²",
			rawTotal: Math.ceil(area),
			status: "visual-backed",
			formula: "ceil(width * length)",
			variables: [{ label: "area", value: area.toFixed(2) }],
			notes: [
				"Trossbottenskiva (asfaboard) uttryckt i full golvyta — skivorna kapas i fackbredder och spillet vid bjälklinjerna är inte återanvändbart.",
			],
		},
		{
			id: "two-room-subfloor-ledgers",
			category: "structure.subfloor-ledgers",
			sourceLabel: "Trossbottenläkt",
			sourceType: "material",
			dimension: "28×70 mm",
			unit: "lpm",
			rawTotal: Math.ceil(resolveSubfloorLedgerLm(W, L, JOIST_W, CC)),
			status: "visual-backed",
			formula: "ceil(2 * bayCount * length)",
			variables: [
				{
					label: "bayCount",
					value: resolveSubfloorBayCount(W, JOIST_W, CC).toString(),
				},
				{ label: "length", value: L.toFixed(2) },
			],
			notes: ["En läkt på varje bjälksida i varje fack, hela byggnadslängden."],
		},
		...interiorBenchRows("two-room", calculation, finishes),
	];
}
